import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import LeadStatusStep from "./LeadStatusStep";
import ErrorModal from "../../utils/ErrorModal";
import ButtonBack from "../../utils/ButtonBack";
import ThemedDatePicker from "../../utils/ThemedDatePicker";

/* ---------- Direction C tokens (styling only) ---------- */
const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const MONO = "'IBM Plex Mono', ui-monospace, monospace";
const INK = "#0F1B2A";
const INK_2 = "#3D4C60";
const MUTED = "#66748A";
const LINE = "#E4E9EF";
const FIELD_EDGE = "#DDE4EC";
const FIELD_BG = "#F8FAFC";
const PRIMARY = "#0B5C8A";
const PRIMARY_DARK = "#084767";
const ERROR = "#C42B24";

// Section accent dots
const SEC_COLORS = {
  fields: "#2F7FD1",
  details: "#12A08F",
};

/* ---------- shared visual styles (values only — no logic) ---------- */
const card = { background: "#FFFFFF", border: `1px solid ${LINE}`, borderRadius: 14, marginBottom: 14 };
const cardHead = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "13px 16px", borderBottom: `1px solid ${LINE}` };
const cardTitle = { display: "flex", alignItems: "center", gap: 9, fontFamily: FONT, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: INK_2 };
const dot = (c) => ({ width: 6, height: 6, flex: "0 0 6px", borderRadius: "50%", background: c });
const cardMeta = { fontFamily: FONT, fontSize: 12, color: MUTED };
const labelStyle = { display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 6, fontFamily: FONT, fontSize: 12, fontWeight: 600, color: INK };
const req = { color: ERROR, marginLeft: 3 };
const inputStyle = (hasError, mono) => ({
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  height: 36,
  padding: "0 12px",
  margin: 0,
  background: FIELD_BG,
  border: `1px solid ${hasError ? ERROR : FIELD_EDGE}`,
  borderRadius: 8,
  fontFamily: mono ? MONO : FONT,
  fontSize: mono ? 13 : 13.5,
  color: INK,
  outline: "none",
});
const errorStyle = { display: "block", margin: "4px 0 0", fontFamily: FONT, fontSize: 11.5, fontWeight: 500, color: ERROR };
const primaryBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: `${PRIMARY} !important`, color: "#FFFFFF !important", border: "0 !important",
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none", whiteSpace: "nowrap",
  "&:hover": { background: `${PRIMARY_DARK} !important` }, "&.Mui-disabled": { opacity: 0.6 },
};
const ghostBtn = {
  height: "38px !important", minHeight: "38px !important", px: 2, margin: "0 !important", borderRadius: "10px !important",
  background: "#FFFFFF !important", color: `${INK} !important`, border: `1px solid ${FIELD_EDGE} !important`,
  fontFamily: FONT, fontSize: 14, fontWeight: 600, textTransform: "none", boxShadow: "none",
  "&:hover": { background: "#F4F7FA !important" },
};
const cols = (n) => ({
  display: "grid",
  gap: "16px 12px",
  gridTemplateColumns: { xs: "minmax(0,1fr)", sm: "repeat(2, minmax(0,1fr))", lg: `repeat(${n}, minmax(0,1fr))` },
});

// Scoped resets so global App.css rules (form, border-container,
// phone-container, inputs, labels) can't squeeze or restyle this page.
const SCOPED_CSS = `
.dc-org .border-container,.dc-org form.form,.dc-org .dashboard-container,.dc-org .add-forms{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;box-shadow:none!important;background:transparent!important;border:0!important;box-sizing:border-box!important;}
.dc-org .dc-card{width:100%!important;max-width:none!important;margin:0 0 14px 0!important;padding:0!important;box-shadow:none!important;box-sizing:border-box!important;}
.dc-org .dc-card label{width:auto!important;margin-top:0!important;font-size:12px!important;line-height:1.4!important;}
.dc-org .dc-card input:not([type=checkbox]){width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;height:36px!important;font-size:13.5px!important;}
.dc-org .dc-card .MuiInputBase-root input{height:auto!important;width:auto!important;flex:1 1 60px;}
.dc-org .dc-card .MuiAutocomplete-root,.dc-org .dc-card .MuiInputBase-root{width:100%!important;max-width:none!important;}
.dc-org .dc-card .MuiFormControlLabel-root{width:100%!important;}
.dc-org .dc-card .phone-container{width:100%!important;max-width:none!important;margin:0!important;padding:0!important;}
.dc-org .dc-card input.dc-bare{height:100%!important;border:0!important;background:transparent!important;flex:1 1 auto;min-width:0;}
.dc-org .dc-card input::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-cc *{font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;font-size:13.5px!important;color:#0F1B2A;}
.dc-org .dc-card p.error{margin:4px 0 0!important;}
.dc-org .dc-head-btns{display:flex!important;flex-direction:row!important;margin:0!important;padding:0!important;align-items:center!important;align-self:flex-end!important;}
.dc-org .dc-head-btns .MuiButton-root,.dc-org .dc-head-btns button{margin:0!important;height:38px!important;min-height:38px!important;max-height:38px!important;padding-top:0!important;padding-bottom:0!important;line-height:1!important;position:static!important;inset:auto!important;top:auto!important;bottom:auto!important;transform:none!important;translate:none!important;align-self:center!important;margin-block:0!important;vertical-align:middle!important;box-sizing:border-box!important;}
`;

// Extra resets for this page (textarea + step badge)
const PAGE_CSS = `
.dc-org .dc-card textarea{width:100%!important;max-width:none!important;margin:0!important;box-sizing:border-box!important;font-size:13.5px!important;font-family:'Plus Jakarta Sans',system-ui,sans-serif!important;}
.dc-org .dc-card textarea::placeholder{color:#8A97A8;opacity:1;}
.dc-org .dc-card label.project-checkbox{margin:0!important;width:100%!important;font-size:13px!important;}
.dc-org .form-step{margin:0!important;}
`;

const stepBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 20,
  height: 20,
  borderRadius: "50%",
  background: "#EAF2FB",
  border: "1px solid #C9DEF3",
  color: "#1B4F82",
  fontFamily: MONO,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0,
  flexShrink: 0,
  margin: 0,
};
const acSx = {
  margin: 0,
  "& .MuiInputBase-root": {
    minHeight: 36,
    fontFamily: FONT,
    fontSize: "13.5px",
    borderRadius: "8px",
    padding: "0 8px !important",
    background: FIELD_BG,
  },
  "& .MuiInputBase-root.Mui-focused": { background: "#FFFFFF" },
  "& .MuiInputBase-input": { padding: "7px 4px !important" },
  "& input::placeholder": { color: "#8A97A8", opacity: 1 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: FIELD_EDGE },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#B4C6DA" },
  "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: PRIMARY, borderWidth: 1 },
  "& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ERROR },
  "& .MuiFormHelperText-root": { display: "none" },
};

const ALL_FIELDS = [
  { label: "Project Name", name: "projectName", type: "text" },
  { label: "Builder Name", name: "builder", type: "text" },
  { label: "Location", name: "location", type: "text" },
  { label: "RERA Number", name: "reraNumber", type: "text" },
  { label: "Total Units", name: "totalUnits", type: "number" },
  { label: "Launch Date", name: "launchDate", type: "date" },
  { label: "Possession Date", name: "possessionDate", type: "date" },
  { label: "Status", name: "status", type: "text" },
  { label: "Base Price", name: "basePrice", type: "number" },
  { label: "Website URL", name: "websiteUrl", type: "text" },
  { label: "Brochure URL", name: "brochureUrl", type: "text" },
  { label: "Description", name: "description", type: "textarea" },
];

// Placeholders (display only)
const PLACEHOLDERS = {
  projectName: "Project name",
  builder: "Builder / developer",
  location: "Area, city",
  reraNumber: "P52100012345",
  totalUnits: "0",
  status: "Under construction",
  basePrice: "0",
  websiteUrl: "https://",
  brochureUrl: "https://",
  description: "Short description of the project...",
};

const STATUS_OPTIONS = [
  "Interested",
  "Site Visit Done",
  "Negotiation",
  "Won",
  "Not Interested",
];

const CreateProject = () => {
  const [selectedFields, setSelectedFields] = useState([
    "projectName",
    "builder",
    "location",
  ]);
  const [formValues, setFormValues] = useState({});
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [teams, setTeams] = useState();
  const [teamId, setTeamId] = useState(null);
  const [step, setStep] = useState(1);
  const [metaStatus, setMetaStatus] = useState();
  const [project, setProject] = useState();
  const [statusRows, setStatusRows] = useState([
    { status: "New", meta: "New Lead" },
    { status: "Visit completed", meta: "Site Visit" },
    { status: "Proposal sent", meta: "Negotiation" },
    { status: "Booking confirmed", meta: "Committed" },
    { status: "Registered", meta: "Closed - Won" },
    { status: "Not interested", meta: "Closed - Lost" },
  ]);
  const [newStatus, setNewStatus] = useState({
    status: "",
    meta: "",
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState({});
  const orgId = JSON.parse(localStorage.getItem("org"));
  const isMobile = useMediaQuery("(max-width: 750px)");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState("");
  const [modalType, setModalType] = useState("success");
  const afterClose = useRef(null);

  const showModal = (msg, type, onClose = null) => {
    setModalMsg(msg);
    setModalType(type);
    afterClose.current = onClose;
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    const next = afterClose.current;
    afterClose.current = null;
    if (next) next();
  };

  useEffect(() => {
    const getTeams = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/team/all?orgId=${orgId}`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setTeams(res.data);
        if (res.data.length === 0) {
          setOpen(true);
        }
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getTeams();

    const getMeta = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/meta-lead/all`,
          { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
        );
        setMetaStatus(res.data);
      } catch (error) {
        console.log(error.response.data.Exception || "Access denied");
      }
    };
    getMeta();
  }, []);

  const REQUIRED_FIELDS = ["projectName", "builder", "location"];

  const handleCheckboxChange = (name) => {
    if (REQUIRED_FIELDS.includes(name)) return;
    setSelectedFields((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name],
    );
  };

  const handleInputChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async () => {
    const newErrors = {};

    // Required fields validation
    REQUIRED_FIELDS.forEach((field) => {
      if (!formValues[field] || formValues[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });

    // Team validation
    if (!teamId) {
      newErrors.teamId = "This field is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = {
      ...formValues,
      orgId: orgId,
      selectedFields: selectedFields,
      teamId: teamId,
    };
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/project`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } },
      );
      // navigate("/dashboard/projects");
      setProject(res.data);
      setStep(2);
    } catch (error) {
      console.log(error);
      showModal(error?.response?.data?.Exception || "Server Error", "error");
    } finally {
      setLoading(false);
    }
  };

  const addStatusRow = () => {
    setStatusRows((prev) => [...prev, { status: "", metaStatusId: "" }]);
  };

  const updateStatusRow = (index, field, value) => {
    const updated = [...statusRows];
    updated[index][field] = value;
    setStatusRows(updated);
  };

  const removeStatusRow = (index) => {
    setStatusRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveStatuses = async () => {
    const validRows = statusRows.filter((r) => r.status && r.meta);

    if (validRows.length === 0) {
      showModal("Please add at least one status", "error");
      return;
    }

    try {
      setLoading(true);
      for (const row of validRows) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/lead-status`,
          {
            projectId: project.projectId,
            status: row.status,
            meta: row.meta,
          },
          {
            headers: {
              Authorization: `Bearer ${storedUser.accessToken}`,
            },
          },
        );
      }

      showModal("All lead statuses added successfully", "success", () =>
        navigate("/dashboard/projects"),
      );
    } catch (error) {
      console.error(error);
      showModal(
        error?.response?.data?.Exception ||
          "Failed while adding one of the statuses",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/dashboard/projects`);
  };

  /* ---------- markup helpers (same handlers as before) ---------- */
  const section = (id, num, title, meta, children, n = 3, hint) => (
    <section key={id} className="dc-card" style={card}>
      <div style={cardHead}>
        <span style={cardTitle}>
          <span className="form-step" style={stepBadgeStyle}>{num}</span>
          {title}
        </span>
        <span style={cardMeta}>{meta}</span>
      </div>
      <Box sx={{ p: "16px 16px 18px" }}>
        {hint && (
          <div style={{ margin: "-4px 0 12px", fontFamily: FONT, fontSize: 12.5, color: MUTED }}>{hint}</div>
        )}
        <Box sx={cols(n)}>{children}</Box>
      </Box>
    </section>
  );

  const fieldChip = (field) => {
    const on = selectedFields.includes(field.name);
    const locked = REQUIRED_FIELDS.includes(field.name);
    return (
      <label
        key={field.name}
        className={`project-checkbox ${
          selectedFields.includes(field.name) ? "checkbox-active" : ""
        }`}
        title={locked ? "Always included" : undefined}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          boxSizing: "border-box",
          height: 36,
          padding: "0 12px",
          margin: 0,
          background: on ? "#EAF2FB" : FIELD_BG,
          border: `1px solid ${on ? "#8FC3E6" : FIELD_EDGE}`,
          borderRadius: 8,
          color: on ? "#1B4F82" : INK_2,
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: on ? 600 : 500,
          lineHeight: 1.3,
          cursor: locked ? "not-allowed" : "pointer",
          userSelect: "none",
          transition: "background .15s, border-color .15s",
        }}
      >
        <input
          type="checkbox"
          checked={selectedFields.includes(field.name)}
          disabled={REQUIRED_FIELDS.includes(field.name)}
          onChange={() => handleCheckboxChange(field.name)}
          style={{ width: "auto", display: "none" }}
        />
        <span
          aria-hidden="true"
          style={{
            flex: "0 0 14px",
            width: 14,
            height: 14,
            borderRadius: 3,
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: on ? (locked ? "#8494A8" : PRIMARY) : "#FFFFFF",
            border: `1.5px solid ${on ? (locked ? "#8494A8" : PRIMARY) : "#9AA7B8"}`,
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1,
          }}
        >
          {on ? "✓" : ""}
        </span>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {field.label}
        </span>
      </label>
    );
  };

  const detailField = (fieldName) => {
    const field = ALL_FIELDS.find((f) => f.name === fieldName);

    if (!field) return null;

    const err = errors[field.name];
    const isNum = field.type === "number";

    return (
      <Box key={field.name} sx={field.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}>
        <label style={labelStyle}>
          {field.label}
          {REQUIRED_FIELDS.includes(field.name) && <span style={req}>*</span>}
        </label>

        {field.type === "textarea" ? (
          <textarea
            className={`${err && "input-error"}`}
            value={formValues[field.name] || ""}
            placeholder={PLACEHOLDERS[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            style={{
              ...inputStyle(!!err),
              height: "auto",
              minHeight: 80,
              padding: "9px 12px",
              lineHeight: 1.5,
              resize: "vertical",
            }}
            rows={3}
          />
        ) : field.type === "date" ? (
          <ThemedDatePicker
            name={field.name}
            value={formValues[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            error={!!err}
          />
        ) : (
          <input
            className={`${err && "input-error"}`}
            type={field.type}
            value={formValues[field.name] || ""}
            placeholder={PLACEHOLDERS[field.name] || ""}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            style={inputStyle(!!err, isNum)}
          />
        )}
        {err && (
          <span className="error-text" style={errorStyle}>
            {err}
          </span>
        )}
      </Box>
    );
  };

  const teamField = (
    <Box key="teamId">
      <label htmlFor="team" style={labelStyle}>
        Assign Team<span style={req}>*</span>
      </label>
      <Autocomplete
        name="team"
        className={`${errors.teamId && "input-error"}`}
        options={teams || []}
        getOptionLabel={(option) => option.name || ""}
        value={teams?.find((t) => t.teamId === teamId) || null}
        onChange={(e, value) => {
          setTeamId(value?.teamId || "");
        }}
        isOptionEqualToValue={(option, value) =>
          option.teamId === value.teamId
        }
        renderInput={(params) => (
          <TextField
            {...params}
            name="teamId"
            placeholder="Select Team"
            error={!!errors.teamId}
            helperText={errors.teamId}
          />
        )}
        sx={acSx}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "12px",
              border: `1px solid ${LINE}`,
              boxShadow: "0 16px 36px -12px rgba(15,27,42,0.24)",
              "& .MuiAutocomplete-option": {
                fontFamily: FONT,
                fontSize: 13.5,
                borderRadius: "8px",
                mx: 0.75,
              },
            },
          },
        }}
      />
      {errors.teamId && (
        <span className="error-text" style={errorStyle}>{errors.teamId}</span>
      )}
    </Box>
  );

  const saveButton = (
    <Button
      className="btn-contained"
      onClick={handleSubmit}
      disabled={loading}
      sx={primaryBtn}
    >
      {loading ? "Saving..." : "Submit Project"}
    </Button>
  );

  // Details: selected fields (textarea last so it spans the full row), then team
  const detailNames = [
    ...selectedFields.filter((n) => n !== "description"),
    ...(selectedFields.includes("description") ? ["description"] : []),
  ];

  return (
    <>
      <style>{SCOPED_CSS + PAGE_CSS}</style>
      <div className="dc-org" style={{ fontFamily: FONT, color: INK, textAlign: "left" }}>
        {step === 1 && (
          <div style={{ padding: isMobile ? "4px 0 88px" : "4px 0 32px" }}>
            {/* ---------------- PAGE HEADER ---------------- */}
            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
              <div style={{ minWidth: 0 }}>
                <button
                  type="button"
                  onClick={handleBack}
                  style={{ border: 0, background: "transparent", padding: 0, marginBottom: 14, cursor: "pointer", fontFamily: FONT, fontSize: 13, fontWeight: 600, color: PRIMARY }}
                >
                  ← Projects
                </button>
                <Typography
                  component="h1"
                  sx={{ m: 0, fontFamily: FONT, fontSize: isMobile ? 24 : 28, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.15, color: INK }}
                >
                  Create project
                </Typography>
                <div style={{ marginTop: 6, fontSize: 13.5, color: INK_2 }}>
                  Step 1 of 2 · Project details, then lead statuses
                </div>
              </div>
              {!isMobile && (
                <Box className="dc-head-btns" sx={{ display: "flex", alignItems: "center", gap: 1.25, flex: "0 0 auto" }}>
                  <Button className="btn-outlined" onClick={handleBack} sx={ghostBtn}>
                    Cancel
                  </Button>
                  {saveButton}
                </Box>
              )}
            </Box>

            <div className="add-forms">
            {/* ---------------- 1. FIELDS ---------------- */}
            {section(
              "fields",
              1,
              "Select fields for project",
              `${selectedFields.length} of ${ALL_FIELDS.length} selected`,
              ALL_FIELDS.map(fieldChip),
              6,
              "Project Name, Builder Name and Location are always included.",
            )}

            {/* ---------------- 2. DETAILS ---------------- */}
            {section(
              "details",
              2,
              "Project details",
              "4 required",
              [...detailNames.map(detailField), teamField],
              3,
            )}
            </div>

            {/* ---------------- MOBILE ACTION BAR ---------------- */}
            {isMobile && (
              <Box
                className="btn-container"
                sx={{
                  position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 30, display: "flex", alignItems: "center", gap: 1.25,
                  p: "10px 16px", background: "#FFFFFF", borderTop: `1px solid ${LINE}`, boxShadow: "0 -8px 24px -16px rgba(15,27,42,0.3)",
                }}
              >
                <ButtonBack handleBack={handleBack} />
                <Box sx={{ flex: 1, display: "flex", "& > *": { flex: 1 } }}>{saveButton}</Box>
              </Box>
            )}
          </div>
        )}
        {step === 2 && (
          <LeadStatusStep
            project={project}
            loading={loading}
            onSubmit={async (rows) => {
              if (rows.length === 0) {
                showModal("Please select at least one status", "error");
                return;
              }
              try {
                setLoading(true);
                for (const row of rows) {
                  await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/lead-status`,
                    { projectId: project.projectId, status: row.status, meta: row.meta },
                    { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
                  );
                }
                showModal("All lead statuses added successfully", "success", () =>
                  navigate("/dashboard/projects"),
                );
              } catch (error) {
                showModal(error?.response?.data?.Exception || "Failed while adding statuses", "error");
              } finally {
                setLoading(false);
              }
            }}
          />
        )}

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "18px",
              p: 1,
              textAlign: "center",
              fontFamily: FONT,
              boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)",
            },
          }}
          slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
        >
          {/* Close button (top-right) */}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              width: 30,
              height: 30,
              background: "#F4F7FA",
              border: `1px solid ${LINE}`,
              borderRadius: "9px",
              color: "#4A5A6E",
              "&:hover": { background: "#EAF2FB" },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>

          {/* Modal Content */}
          <DialogContent sx={{ pt: 4 }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="180px"
              gap={2.5}
            >
              {/* Message */}
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ fontFamily: FONT, fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.35, color: INK, textWrap: "pretty" }}
              >
                Please make sure you have a team for the project you are creating.
              </Typography>

              <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
                {/* CTA Button */}
                <Button
                  className="btn-contained"
                  fullWidth
                  onClick={() => navigate("/dashboard/create-team")}
                  sx={{ ...primaryBtn, height: "44px !important", minHeight: "44px !important" }}
                >
                  Create Team
                </Button>
                <Button
                  className="btn-outlined"
                  fullWidth
                  onClick={() => setOpen(false)}
                  sx={{ ...ghostBtn, height: "44px !important", minHeight: "44px !important" }}
                >
                  I Already Have A Team
                </Button>
              </div>
            </Box>
          </DialogContent>
        </Dialog>

        <ErrorModal
          open={modalOpen}
          handleClose={handleModalClose}
          msg={modalMsg}
          type={modalType}
        />
      </div>
    </>
  );
};

export default CreateProject;